import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Upload, Save, Trash2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { BUSINESS_UNITS } from '@/constants/businessUnits';

const BU_KEYS = ['chipchip', 'zeami', 'export', 'grantt', 'zarely'] as const;
type BUKey = typeof BU_KEYS[number];

interface ParsedRow {
  emp_id: string | null;
  full_name: string;
  chipchip: number;
  zeami: number;
  export: number;
  grantt: number;
  zarely: number;
}

interface SavedRow extends ParsedRow {
  id?: string;
  is_active: boolean;
}

interface MatchInfo {
  matched: number;
  unmatched: number;
  unmatchedNames: string[];
  expenseSampleByName: Record<string, number>;
}

const MONTHS = '(january|february|march|april|may|june|july|august|september|october|november|december)';
const normalize = (s: string) => {
  let v = (s || '');
  v = v.replace(/^(\s*salary\s*[-:]\s*)+/i, '');
  v = v.replace(/^(salary\s+payment\s+for|salary\s+for|salary\s+to|payment\s+for)\s+/i, '');
  v = v.replace(new RegExp(`^${MONTHS}\\s+salary\\s*[-:]?\\s*`, 'i'), '');
  v = v.replace(new RegExp(`\\s*[-:]\\s*(?:${MONTHS}\\s*)?salary\\s*$`, 'i'), '');
  return v.toLowerCase().replace(/\s+/g, ' ').trim();
};

// First name + father name key (first two tokens of normalized name)
const nameKey = (s: string) => {
  const n = normalize(s);
  if (!n) return '';
  const parts = n.split(' ').filter(Boolean);
  return parts.slice(0, 2).join(' ');
};

// flexible header detection
const findKey = (row: any, candidates: string[]): string | undefined => {
  const keys = Object.keys(row);
  return keys.find((k) =>
    candidates.some((c) => k.toLowerCase().replace(/\s|_|%/g, '').includes(c))
  );
};

const parseSheet = (file: File): Promise<ParsedRow[]> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows: any[] = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (!rows.length) return resolve([]);
        const sample = rows[0];
        const empKey = findKey(sample, ['empid', 'employeeid']);
        const nameKey = findKey(sample, ['fullname', 'name']);
        const ccKey = findKey(sample, ['cc', 'chipchip']);
        const zeKey = findKey(sample, ['zeami']);
        const exKey = findKey(sample, ['exp', 'export']);
        const grKey = findKey(sample, ['grant']);
        const zrKey = findKey(sample, ['zrl', 'zarely']);
        if (!nameKey) throw new Error('Could not find a Full Name column');
        const parsed: ParsedRow[] = rows
          .map((r) => {
            const num = (k?: string) => {
              if (!k) return 0;
              const v = r[k];
              if (typeof v === 'number') return v <= 1 ? v * 100 : v;
              const cleaned = String(v ?? '').replace('%', '').trim();
              const n = parseFloat(cleaned);
              return isNaN(n) ? 0 : (n <= 1 && n > 0 ? n * 100 : n);
            };
            return {
              emp_id: empKey ? String(r[empKey] || '').trim() || null : null,
              full_name: String(r[nameKey] || '').trim(),
              chipchip: num(ccKey),
              zeami: num(zeKey),
              export: num(exKey),
              grantt: num(grKey),
              zarely: num(zrKey),
            };
          })
          .filter((r) => r.full_name);
        resolve(parsed);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('File read error'));
    reader.readAsArrayBuffer(file);
  });

const sumRow = (r: ParsedRow) =>
  Math.round((r.chipchip + r.zeami + r.export + r.grantt + r.zarely) * 100) / 100;

const EmployeeBUAllocationsPage: React.FC = () => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [savedRows, setSavedRows] = useState<SavedRow[]>([]);
  const [matchInfo, setMatchInfo] = useState<MatchInfo | null>(null);

  const loadSaved = async () => {
    const { data, error } = await (supabase as any)
      .from('employee_bu_allocations')
      .select('id, emp_id, full_name, chipchip, zeami, export, grantt, zarely, is_active')
      .order('full_name')
      .limit(1000);
    if (error) {
      toast.error(error.message);
      return;
    }
    setSavedRows((data || []) as SavedRow[]);
  };

  useEffect(() => {
    loadSaved();
  }, []);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    try {
      const rows = await parseSheet(file);
      if (!rows.length) {
        toast.error('No rows found in sheet');
        return;
      }
      setParsedRows(rows);
      // Match against existing salary expenses (by category name containing "Salary"
      // OR description starting with "Salary"). Prefer vendor_name, fall back to description.
      const norms = rows.map((r) => normalize(r.full_name));
      const keys = rows.map((r) => nameKey(r.full_name));
      const { data: exps } = await (supabase as any)
        .from('expenses')
        .select('description, vendor_name, expense_date, category:expense_categories(name)')
        .or('description.ilike.Salary %,description.ilike.Salary-%,description.ilike.Salary:%,description.ilike.Salary to %,description.ilike.%Salary payment for %')
        .limit(10000);
      const { data: catExps } = await (supabase as any)
        .from('expenses')
        .select('description, vendor_name, expense_date, category:expense_categories!inner(name)')
        .ilike('category.name', '%salary%')
        .limit(10000);
      const allExps = [...(exps || []), ...(catExps || [])];
      const counts: Record<string, number> = {};
      const keyCounts: Record<string, number> = {};
      const seen = new Set<string>();
      allExps.forEach((ex: any) => {
        const dedupKey = `${ex.description}|${ex.expense_date}|${ex.vendor_name}`;
        if (seen.has(dedupKey)) return;
        seen.add(dedupKey);
        const candidate = (ex.vendor_name && String(ex.vendor_name).trim()) || ex.description || '';
        const n = normalize(candidate);
        if (!n) return;
        counts[n] = (counts[n] || 0) + 1;
        const k = nameKey(candidate);
        if (k) keyCounts[k] = (keyCounts[k] || 0) + 1;
      });
      // Detect duplicate first+father keys among the uploaded rows — fallback only safe when unique
      const sheetKeyCounts: Record<string, number> = {};
      keys.forEach((k) => { if (k) sheetKeyCounts[k] = (sheetKeyCounts[k] || 0) + 1; });

      let matched = 0;
      const unmatchedNames: string[] = [];
      const sample: Record<string, number> = {};
      norms.forEach((n, i) => {
        const k = keys[i];
        if (counts[n] > 0) {
          matched++;
          sample[rows[i].full_name] = counts[n];
        } else if (k && sheetKeyCounts[k] === 1 && keyCounts[k] > 0) {
          matched++;
          sample[rows[i].full_name] = keyCounts[k];
        } else {
          unmatchedNames.push(rows[i].full_name);
        }
      });
      setMatchInfo({
        matched,
        unmatched: unmatchedNames.length,
        unmatchedNames,
        expenseSampleByName: sample,
      });
      toast.success(`Parsed ${rows.length} employees`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to parse file');
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const validRows = useMemo(() => parsedRows.filter((r) => sumRow(r) === 100), [parsedRows]);
  const invalidRows = useMemo(() => parsedRows.filter((r) => sumRow(r) !== 100), [parsedRows]);

  const handleSave = async () => {
    if (!validRows.length) {
      toast.error('No valid rows to save (each must sum to 100%)');
      return;
    }
    setSaving(true);
    try {
      const payload = validRows.map((r) => ({
        emp_id: r.emp_id,
        full_name: r.full_name,
        chipchip: r.chipchip,
        zeami: r.zeami,
        export: r.export,
        grantt: r.grantt,
        zarely: r.zarely,
        is_active: true,
      }));
      // upsert by normalized_name (unique). Supabase upsert needs onConflict on a real column — normalized_name is generated unique.
      const { error } = await (supabase as any)
        .from('employee_bu_allocations')
        .upsert(payload, { onConflict: 'normalized_name' });
      if (error) throw error;
      toast.success(`Saved ${validRows.length} employee allocations`);
      setParsedRows([]);
      setMatchInfo(null);
      await loadSaved();
    } catch (err: any) {
      toast.error(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const applyToExisting = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const start = new Date();
    start.setDate(1);
    const from = start.toISOString().slice(0, 10);
    const { data, error } = await (supabase as any).rpc('apply_employee_allocations_to_salaries', {
      p_from: from,
      p_to: today,
      p_overwrite: false,
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    const r = data || {};
    toast.success(
      `Matched ${r.matched_count || 0} · Skipped ${r.skipped_existing || 0} · Unmatched ${r.unmatched_count || 0}`
    );
  };

  const removeRow = async (id?: string) => {
    if (!id) return;
    if (!confirm('Delete this allocation?')) return;
    const { error } = await (supabase as any).from('employee_bu_allocations').delete().eq('id', id);
    if (error) {
      toast.error(error.message);
      return;
    }
    await loadSaved();
  };

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Employee BU Allocations</h1>
          <p className="text-sm text-muted-foreground">
            Upload an XLSX/CSV with per-employee splits across business units. Salary expenses are
            automatically allocated when imported.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>1. Upload allocation sheet</CardTitle>
            <CardDescription>
              Required columns: Full Name, CC%, ZEAMI%, EXP%, Grant%, ZRL%. Emp ID is optional. Each row
              must sum to 100%.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFile}
                className="hidden"
              />
              <Button onClick={() => fileRef.current?.click()} disabled={parsing}>
                {parsing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                Choose file
              </Button>
              {parsedRows.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {parsedRows.length} parsed · {validRows.length} valid · {invalidRows.length} invalid
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {matchInfo && (
          <Card>
            <CardHeader>
              <CardTitle>2. Review matches against existing salary expenses</CardTitle>
              <CardDescription>
                Matching uses the normalized full name first, then falls back to first name + father
                name (first two tokens) when that pair is unique. Unmatched employees can still be
                saved — they just won't auto-allocate until a matching salary expense is created.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-3">
                <Badge variant="default" className="gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Matched: {matchInfo.matched}
                </Badge>
                <Badge variant="destructive" className="gap-1">
                  <AlertCircle className="h-3 w-3" /> Unmatched: {matchInfo.unmatched}
                </Badge>
              </div>
              {matchInfo.unmatched > 0 && (
                <div>
                  <div className="text-sm font-medium mb-1">Unmatched names:</div>
                  <div className="text-xs text-muted-foreground max-h-32 overflow-auto border rounded p-2">
                    {matchInfo.unmatchedNames.join(', ')}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {parsedRows.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>3. Preview &amp; save</CardTitle>
              <CardDescription>
                Rows with sum ≠ 100% are highlighted and will be skipped.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border max-h-[500px] overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background">
                    <TableRow>
                      <TableHead>Emp ID</TableHead>
                      <TableHead>Full Name</TableHead>
                      <TableHead className="text-right">CC%</TableHead>
                      <TableHead className="text-right">Zeami%</TableHead>
                      <TableHead className="text-right">Export%</TableHead>
                      <TableHead className="text-right">Grantt%</TableHead>
                      <TableHead className="text-right">Zarely%</TableHead>
                      <TableHead className="text-right">Sum</TableHead>
                      <TableHead>Match</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedRows.map((r, i) => {
                      const sum = sumRow(r);
                      const ok = sum === 100;
                      const matchCount = matchInfo?.expenseSampleByName[r.full_name] || 0;
                      return (
                        <TableRow key={i} className={ok ? '' : 'bg-destructive/10'}>
                          <TableCell className="text-xs">{r.emp_id || '—'}</TableCell>
                          <TableCell>{r.full_name}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.chipchip}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.zeami}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.export}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.grantt}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.zarely}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            <Badge variant={ok ? 'default' : 'destructive'}>{sum}</Badge>
                          </TableCell>
                          <TableCell>
                            {matchCount > 0 ? (
                              <Badge variant="outline" className="text-xs">
                                {matchCount} salary
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="flex gap-2 pt-3">
                <Button onClick={handleSave} disabled={saving || !validRows.length}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Save {validRows.length} valid rows
                </Button>
                <Button variant="outline" onClick={() => { setParsedRows([]); setMatchInfo(null); }}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Saved allocations ({savedRows.length})</CardTitle>
                <CardDescription>
                  These power auto-allocation on new salary imports.
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={applyToExisting}>
                Apply to current month
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border max-h-[600px] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead>Emp ID</TableHead>
                    <TableHead>Full Name</TableHead>
                    {BUSINESS_UNITS.map((bu) => (
                      <TableHead key={bu.value} className="text-right">
                        {bu.label}%
                      </TableHead>
                    ))}
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {savedRows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs">{r.emp_id || '—'}</TableCell>
                      <TableCell>{r.full_name}</TableCell>
                      {BU_KEYS.map((k) => (
                        <TableCell key={k} className="text-right tabular-nums">
                          {(r as any)[k]}
                        </TableCell>
                      ))}
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeRow(r.id)}
                          className="h-7 w-7 p-0"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!savedRows.length && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                        No saved allocations yet. Upload a sheet above.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default EmployeeBUAllocationsPage;
